import { createResponsiveSlider } from '../utils/responsive-slider';

// Desktop with a mouse: cards form a hover deck. Touch devices and screens
// below 1024px: the same cards become a Splide slider with a progress bar.
// Keep this query in sync with the deck/slider media queries in _home.scss.
const SLIDER_QUERY = '(max-width: 1023px), (hover: none)';

const initCategoryDeck = (section) => {
  if (section.dataset.categoryDeckReady === 'true') {
    return;
  }

  const cards = Array.from(
    section.querySelectorAll('[data-category-deck-card]')
  );
  const cardsContainer = section.querySelector('.home-categories__cards');
  const track = section.querySelector('.home-categories__track');
  const list = section.querySelector('.home-categories__list');
  const slides = Array.from(
    section.querySelectorAll('.home-categories__slide')
  );
  const movableCards = cards.slice(0, -1);
  const sliderQuery = window.matchMedia(SLIDER_QUERY);
  const reducedMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );
  const phaseDuration = 220;
  let activeCard = null;
  let desiredCard = null;
  let isTransitioning = false;
  let transitionVersion = 0;
  let syncFrame = null;
  let resizeFrame = null;

  if (!cardsContainer || !track || !list || !cards.length) {
    return;
  }

  const isDeck = () => !sliderQuery.matches;

  /* Deck ------------------------------------------------------------------ */

  const clearDeckLayout = () => {
    cardsContainer.style.removeProperty('--category-card-step');
    cards.forEach((card) => {
      card.style.removeProperty('--category-card-offset');
      card.style.removeProperty('--category-card-pull-offset');
    });
  };

  const updateDeckLayout = () => {
    if (!isDeck()) {
      clearDeckLayout();
      return;
    }

    const containerStyles = window.getComputedStyle(cardsContainer);
    const horizontalPadding =
      parseFloat(containerStyles.paddingLeft) +
      parseFloat(containerStyles.paddingRight);
    const availableWidth = cardsContainer.clientWidth - horizontalPadding;
    const cardWidth = cards[0].offsetWidth;
    const step =
      cards.length > 1 ? (availableWidth - cardWidth) / (cards.length - 1) : 0;
    const normalizedStep = Math.max(0, step);
    const cardOverlap = Math.max(0, cardWidth - normalizedStep);
    const pullClearance = 12;
    const pullOffset = -(cardOverlap + pullClearance);

    cardsContainer.style.setProperty(
      '--category-card-step',
      `${normalizedStep}px`
    );

    cards.forEach((card, index) => {
      card.style.setProperty(
        '--category-card-offset',
        `${normalizedStep * index}px`
      );

      card.style.setProperty('--category-card-pull-offset', `${pullOffset}px`);
    });
  };

  const waitForPhase = () =>
    new Promise((resolve) => window.setTimeout(resolve, phaseDuration));

  const clearDeckState = () => {
    transitionVersion += 1;
    activeCard = null;
    desiredCard = null;
    isTransitioning = false;
    movableCards.forEach((card) => {
      card.classList.remove('is-deck-pulling', 'is-deck-front');
    });
  };

  // Moves one card through a full out-and-back: it slides out, changes its
  // stacking (front or back) while clear of the deck, then slides home.
  const moveCard = async (card, toFront, isCurrent) => {
    card.classList.add('is-deck-pulling');
    await waitForPhase();
    if (!isCurrent()) return false;

    card.classList.toggle('is-deck-front', toFront);
    card.classList.remove('is-deck-pulling');
    await waitForPhase();

    return isCurrent();
  };

  // Strictly sequential: when moving from one card to another, the current
  // card first finishes its way back into the deck, and only then does the
  // next card come out — so stacking never changes while cards overlap.
  const processDeckState = async () => {
    if (isTransitioning || !isDeck()) {
      return;
    }

    if (reducedMotionQuery.matches) {
      movableCards.forEach((card) => {
        card.classList.toggle('is-deck-front', card === desiredCard);
        card.classList.remove('is-deck-pulling');
      });
      activeCard = desiredCard;
      return;
    }

    isTransitioning = true;
    const version = transitionVersion;
    const isCurrent = () => version === transitionVersion && isDeck();

    while (activeCard !== desiredCard && isCurrent()) {
      if (activeCard) {
        if (!(await moveCard(activeCard, false, isCurrent))) break;
        activeCard = null;
      } else {
        const incoming = desiredCard;
        if (!(await moveCard(incoming, true, isCurrent))) break;
        activeCard = incoming;
      }
    }

    if (version === transitionVersion) {
      isTransitioning = false;
    }
  };

  const syncDesiredCard = () => {
    if (syncFrame !== null) {
      window.cancelAnimationFrame(syncFrame);
    }

    syncFrame = window.requestAnimationFrame(() => {
      syncFrame = null;
      desiredCard =
        movableCards.find((card) => card.matches(':hover')) ||
        movableCards.find((card) => card === document.activeElement) ||
        null;
      processDeckState();
    });
  };

  /* Slider ---------------------------------------------------------------- */

  const slider = createResponsiveSlider({
    section,
    root: cardsContainer,
    track,
    list,
    slides,
    query: SLIDER_QUERY,
    progress: section.querySelector('.home-categories__progress'),
    progressBar: section.querySelector('[data-category-progress]'),
    progressThumb: section.querySelector('[data-category-thumb]'),
    options: { gap: 'clamp(16px, 2.34vw, 24px)' },
    getSlideLabel: (slide) =>
      slide?.querySelector('.home-categories__name')?.textContent.trim(),
  });

  /* Mode switching -------------------------------------------------------- */

  const syncMode = () => {
    if (isDeck()) {
      slider.sync();
      updateDeckLayout();
      return;
    }

    clearDeckState();
    clearDeckLayout();
    slider.sync();
  };

  const requestSync = () => {
    if (resizeFrame !== null) return;

    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = null;
      syncMode();
    });
  };

  cards.forEach((card, index) => {
    card.style.setProperty('--category-card-index', index);
  });

  movableCards.forEach((card) => {
    card.addEventListener('mouseenter', syncDesiredCard);
    card.addEventListener('mouseleave', syncDesiredCard);
    card.addEventListener('focus', syncDesiredCard);
    card.addEventListener('blur', syncDesiredCard);
  });

  sliderQuery.addEventListener?.('change', syncMode);

  if ('ResizeObserver' in window) {
    new ResizeObserver(requestSync).observe(cardsContainer);
  } else {
    window.addEventListener('resize', requestSync);
  }

  window.requestAnimationFrame(syncMode);

  section.dataset.categoryDeckReady = 'true';
};

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-category-deck]').forEach(initCategoryDeck);
});
